import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { ContainerWithBackButton } from '~/components/dialogs/settings/ContainerWithBackButton';
import { Badge } from '~/components/ui/Badge';
import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/Input';
import { Table, TableHead, TableRow } from '~/components/ui/Table';
import { searchDomain } from '~/lib/.client/api/domains';
import { domainsStore, type DomainInformation } from '~/lib/stores/domains';
import { withSpinner } from '~/utils/withSpinner';

export const BuyNewDomain = memo(({ onClose }: { onClose: VoidFunction }) => {
  const domainSearchResult = useStore(domainsStore.domainSearchResult);
  const domainSearchQuery = useStore(domainsStore.domainSearchQuery);
  const setDomainSearchQuery = domainsStore.domainSearchQuery.set;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    /**
     * If we have a custom domain search query, we want to trigger the search immediately.
     */
    if (domainSearchQuery && !domainSearchResult) {
      onSearch();
    }
  }, []);

  const onSearch = useCallback(
    async () =>
      withSpinner({
        action: async () => {
          const result = await searchDomain(domainSearchQuery);

          domainsStore.domainSearchResult.set(result);
        },
        onStateUpdate: setLoading,
      }),
    [domainSearchQuery],
  );

  const onPurchase = useCallback((domain: DomainInformation) => {
    domainsStore.domainPurchaseSelected.set(domain);

    domainsStore.setPurchaseConfirmationStep();
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDomainSearchQuery(event.target.value);
    },
    [setDomainSearchQuery],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSearch();
      }
    },
    [onSearch],
  );

  return (
    <ContainerWithBackButton className="gap-6" title="Find a new domain" onClose={onClose}>
      <p className="text-bolt-ds-textSecondary">Search for a keyword or domain name and we’ll register it for you.</p>

      <div className="flex items-end gap-2 w-full">
        <Input
          label="Domain or keyword"
          placeholder="yourdomain.com"
          className="w-full"
          value={domainSearchQuery}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />

        <Button size="lg" onClick={onSearch} loading={loading} disabled={!domainSearchQuery.trim()}>
          Search
        </Button>
      </div>

      {!loading &&
        domainSearchResult &&
        (!domainSearchResult.success ? (
          <p className="text-red-500 text-left w-full">{domainSearchResult.error}</p>
        ) : (
          <>
            {domainSearchResult.isDomainAvailable === false && (
              <p className="text-orange-500 text-left w-full text-sm">
                <strong>{domainSearchResult.search}</strong> is not available, but here are some close suggestions.
              </p>
            )}

            <Table className="w-full min-h-0 overflow-y-auto">
              <TableHead className="grid grid-cols-[3fr_1fr_1fr_40px] top-0 z-1 sticky">
                <span>Domain</span>
                <span className="justify-self-center">First year</span>
                <span className="justify-self-center">Renewal</span>
                <span className="justify-self-center">Buy</span>
              </TableHead>

              {domainSearchResult.domains.map((item, index) => (
                <TableRow
                  key={index}
                  className="grid grid-cols-[3fr_1fr_1fr_40px] bg-bolt-ds-surface items-center py-0"
                >
                  <span className="text-size-sm font-medium lh-[20px] py-4 flex gap-4">
                    {item.domain}

                    {/* .ai tlds require a 2-year purchase */}
                    {item.domain.endsWith('.ai') && <Badge type="warning">2 years</Badge>}
                  </span>
                  <span className="justify-self-center text-xs">
                    <Badge type="success">${item.price}</Badge>
                  </span>
                  <span className="justify-self-center text-xs">
                    <Badge>${item.renewalPrice}</Badge>
                  </span>
                  <span className="justify-self-center">
                    <Button size="sm" variant="primary" onClick={() => onPurchase(item)}>
                      Buy
                    </Button>
                  </span>
                </TableRow>
              ))}
            </Table>
          </>
        ))}
    </ContainerWithBackButton>
  );
});

BuyNewDomain.displayName = 'BuyNewDomain';
